import React, { type FC, type PropsWithChildren } from 'react';

import { withImage } from '@lucid/hooks';

import Box from '../box/box';
import Text from '../text/text';
import Heading from '../heading/heading';
import ImageWithPlaceholder from '../image/image-with-placeholder';
import Link, { type LinkProps } from '../link/link';
import VisibleLottie from '../lottie/visible-lottie';
import VideoPlayer from '../video/video-player/video-player';
import type { Lucid } from '../../types/types';

export interface StatBackNForthProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Sets the variant for section headings */
  headingVariant?: Lucid.Theme.Component.Typography.VariantKeys;
  /** Reverses the image and text for each component. */
  isReversed?: boolean;
  /** Passes the specific Link component props to the Link component */
  linkProps?: LinkProps;
  /** The content for each section. */
  sections: Lucid.Directus.Generated.Bnf_W_Stat[];
  /** Unique ID for back n forth section */
  sectionId?: string;
  /** Sets the images to be wide. */
  wideImages?: boolean;
}

const StatBackNForth: FC<PropsWithChildren<StatBackNForthProps>> = ({
  className,
  headingVariant = 'heading2XL',
  isReversed = false,
  linkProps,
  sections,
  sectionId,
  wideImages = false,
}) => {
  const bnfSections = sections.filter(section => section.heading && section.image?.filename_disk);

  return (
    <Box
      as="section"
      css={{ label: 'sections' }}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      {bnfSections.map(({ pre_heading, heading, text, image, link, stat_heading, stat_text }, index) => {
        const shouldUseAnimation = image?.type?.includes('application');
        const shouldUseImage = image?.type?.includes('image');
        const shouldUseVideo = image?.type?.includes('video');
        const isOdd = isReversed ? index % 2 === 1 : index % 2 === 0;

        return (
          <Box key={index}>
            <Box
              as="section"
              className="backNForthItem"
              css={theme => [
                {
                  label: 'backNForth',
                  display: 'grid',
                  position: 'relative',
                  gridColumnGap: theme.space.s16,
                  gridTemplateColumns: '1fr',
                  gridTemplateRows: '1fr auto',
                  gridTemplateAreas: '"image" "content"',
                  padding: theme.space.s48,
                  [theme.breakpoints.bp900.min]: {
                    padding: `${theme.space.s64}px ${theme.space.s104}px`,
                    gridTemplateRows: '1fr',
                  },
                  backgroundColor: theme.colors.blue[5],
                  borderRadius: theme.radii.r24,
                },
                isOdd && {
                  [theme.breakpoints.bp768.min]: {
                    gridTemplateAreas: '"content image"',
                    gridTemplateColumns: wideImages ? '0.65fr 1fr' : '1fr 1fr',
                  },
                },
                !isOdd && {
                  [theme.breakpoints.bp768.min]: {
                    gridTemplateAreas: '"image content"',
                    gridTemplateColumns: wideImages ? '1fr 0.5fr' : '1fr 1fr',
                  },
                },
              ]}
              id={`${sectionId}-section-${index + 1}`}
            >
              <Box
                css={{
                  label: 'sectionImageContainer',
                  gridArea: 'image',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  maxHeight: '350px',
                }}
              >
                {shouldUseAnimation && (
                  <VisibleLottie alt={image?.translations?.[0]?.alt} isInitialInView={false} path={withImage(image)} />
                )}
                {shouldUseImage && (
                  <ImageWithPlaceholder
                    alt={image?.translations?.[0]?.alt || ''}
                    aspectRatio="1:1"
                    css={{
                      '& > .image': {
                        maxHeight: '350px',
                        maxWidth: '480px',
                      },
                    }}
                    src={withImage(image)}
                  />
                )}
                {shouldUseVideo && (
                  <Box
                    css={{
                      width: '100%',
                      maxWidth: '480px',
                    }}
                  >
                    <VideoPlayer controls url={withImage(image)} />
                  </Box>
                )}
              </Box>
              <Box
                css={theme => [
                  {
                    label: 'sectionContent',
                    gridArea: 'content',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: `${theme.space.s32}px 0`,
                    maxWidth: '480px',
                    [theme.breakpoints.bp768.min]: {
                      maxWidth: 'initial',
                      alignSelf: 'center',
                    },
                  },
                  isOdd && {
                    justifySelf: 'end',
                    [theme.breakpoints.bp768.min]: {
                      paddingRight: theme.space.s24,
                    },
                  },
                  !isOdd && {
                    justifySelf: 'start',
                    [theme.breakpoints.bp768.min]: {
                      paddingLeft: theme.space.s24,
                    },
                  },
                ]}
              >
                <Text
                  css={theme => ({
                    color: theme.colors.pink[70],
                    fontSize: theme.fontSizes.fs16,
                    fontWeight: theme.fontWeights.fw700,
                  })}
                >
                  {pre_heading}
                </Text>
                <Heading as="h2" css={theme => ({ marginBottom: theme.space.s40 })} variant={headingVariant}>
                  {heading}
                </Heading>
                <Text css={theme => ({ marginBottom: theme.space.s40 })} variant="paragraphS400">
                  {text}
                </Text>
                {link?.final_url && (
                  <Link
                    {...linkProps}
                    css={theme => ({
                      fontSize: theme.fontSizes.fs16,
                    })}
                    href={link?.final_url}
                    iconAnimation="navigational"
                  >
                    {link?.default_link_text}
                  </Link>
                )}
              </Box>
            </Box>
            <Box css={{ display: 'flex', justifyContent: 'center' }}>
              <Box
                css={theme => [
                  {
                    backgroundColor: theme.colors.blue[60],
                    borderRadius: theme.radii.r24,
                    maxWidth: '380px',
                    textAlign: 'center',
                    padding: theme.space.s48,
                    position: 'relative',
                    marginTop: `-${theme.space.s48}px`,
                    marginBottom: theme.space.s40,
                    marginLeft: '16px',
                    marginRight: '16px',
                    [theme.breakpoints.bp900.min]: {
                      marginTop: `-${theme.space.s64}px`,
                      marginBottom: theme.space.s64,
                    },
                  },
                  isOdd && {
                    [theme.breakpoints.bp768.min]: {
                      marginRight: 'auto',
                      marginLeft: theme.space.s40,
                    },
                    [theme.breakpoints.bp900.min]: {
                      marginRight: 'auto',
                      marginLeft: theme.space.s104,
                    },
                  },
                  !isOdd && {
                    [theme.breakpoints.bp768.min]: {
                      marginLeft: 'auto',
                      marginRight: theme.space.s40,
                    },
                    [theme.breakpoints.bp900.min]: {
                      marginLeft: 'auto',
                      marginRight: theme.space.s104,
                    },
                  },
                ]}
              >
                <Text
                  css={theme => ({
                    color: theme.colors.white,
                  })}
                  variant="hero4XL700"
                >
                  {stat_heading}
                </Text>
                <Text
                  css={theme => ({
                    color: theme.colors.white,
                  })}
                >
                  {stat_text}
                </Text>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export { StatBackNForth };
export default StatBackNForth;
